// AXIS ZERO - Effects Module
// VISUAL: Minimal effects, clean sparks, subtle shake
// Particles, screen shake, slow motion, near-miss effects

import { CONFIG } from './Config';

// Near-miss spark particle
class Spark {
  constructor(x, y, color) {
    this.x = x;
    this.y = y;
    this.color = color;
    const angle = Math.random() * Math.PI * 2;
    const speed = 80 + Math.random() * 120;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed;
    this.life = 0.25;
    this.maxLife = 0.25;
    this.size = 2 + Math.random() * 3;
  }

  update(deltaTime) {
    this.x += this.vx * deltaTime;
    this.y += this.vy * deltaTime;
    this.vx *= 0.95;
    this.vy *= 0.95;
    this.life -= deltaTime;
  }

  draw(ctx) {
    const alpha = this.life / this.maxLife;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.color;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 8;
    ctx.fillRect(this.x - this.size / 2, this.y - this.size / 2, this.size, this.size);
    ctx.restore();
  }

  isDead() {
    return this.life <= 0;
  }
}

export class EffectsManager {
  constructor() {
    this.sparks = [];
    
    // Screen shake
    this.shakeIntensity = 0;
    this.shakeDuration = 0;
    this.shakeTimer = 0;
    this.shakeOffsetX = 0;
    this.shakeOffsetY = 0;
    
    // Slow motion
    this.slowMotionActive = false;
    this.slowMotionTimer = 0;
    this.slowMotionDuration = CONFIG.DEATH_SLOW_MO_DURATION;
    this.timeScale = 1;
    
    // Death state
    this.isDying = false;
    
    // Trail speed
    this.trailSpeedMult = 1;
    
    // High streak pulse
    this.highStreakPulse = 0;
  }

  reset() {
    this.sparks = [];
    this.shakeIntensity = 0;
    this.shakeDuration = 0;
    this.shakeTimer = 0;
    this.shakeOffsetX = 0;
    this.shakeOffsetY = 0;
    this.slowMotionActive = false;
    this.slowMotionTimer = 0;
    this.timeScale = 1;
    this.isDying = false;
    this.trailSpeedMult = 1;
    this.highStreakPulse = 0;
  }

  setTrailSpeedMultiplier(mult) {
    this.trailSpeedMult = mult;
  }

  // Spawn near-miss sparks at obstacle position
  spawnNearMissSparks(x, y, color) {
    const count = CONFIG.NEAR_MISS_SPARK_COUNT || 8;
    for (let i = 0; i < count; i++) {
      this.sparks.push(new Spark(x, y, color));
    }
  }

  triggerNearMiss() {
    // Subtle micro shake for near miss
    this.triggerScreenShake(3, 0.08);
  }

  triggerScreenShake(intensity = CONFIG.SCREEN_SHAKE_INTENSITY, duration = CONFIG.SCREEN_SHAKE_DURATION) {
    this.shakeIntensity = intensity;
    this.shakeDuration = duration;
    this.shakeTimer = duration;
  }

  triggerDeathSequence() {
    this.isDying = true;
    this.slowMotionActive = true;
    this.slowMotionTimer = this.slowMotionDuration;
    this.triggerScreenShake(CONFIG.SCREEN_SHAKE_INTENSITY, CONFIG.SCREEN_SHAKE_DURATION);
  }

  setHighStreakPulse(active) {
    if (active && this.highStreakPulse < 0.3) {
      this.highStreakPulse = 0.3;
    }
  }

  update(deltaTime, playerX, playerY, isGrounded, glowMultiplier = 1) {
    // Update slow motion
    if (this.slowMotionActive) {
      this.slowMotionTimer -= deltaTime;
      
      const progress = 1 - (this.slowMotionTimer / this.slowMotionDuration);
      if (progress < 0.2) {
        this.timeScale = 1 - (progress / 0.2) * (1 - CONFIG.DEATH_SLOW_MO_FACTOR);
      } else if (progress < 0.8) {
        this.timeScale = CONFIG.DEATH_SLOW_MO_FACTOR;
      } else {
        this.timeScale = CONFIG.DEATH_SLOW_MO_FACTOR + ((progress - 0.8) / 0.2) * (1 - CONFIG.DEATH_SLOW_MO_FACTOR);
      }
      
      if (this.slowMotionTimer <= 0) {
        this.slowMotionActive = false;
        this.timeScale = 1;
      }
    }
    
    // Update screen shake
    if (this.shakeTimer > 0) {
      this.shakeTimer -= deltaTime;
      const shakeProgress = this.shakeTimer / this.shakeDuration;
      const currentIntensity = this.shakeIntensity * shakeProgress;
      this.shakeOffsetX = (Math.random() - 0.5) * 2 * currentIntensity;
      this.shakeOffsetY = (Math.random() - 0.5) * 2 * currentIntensity;
    } else {
      this.shakeOffsetX = 0;
      this.shakeOffsetY = 0;
    }
    
    // Update sparks
    this.sparks.forEach(s => s.update(deltaTime));
    this.sparks = this.sparks.filter(s => !s.isDead());
    
    // Update high streak pulse
    if (this.highStreakPulse > 0) {
      this.highStreakPulse = Math.max(0, this.highStreakPulse - deltaTime * 2);
    }
  }

  draw(ctx, canvasWidth, canvasHeight, groundY, worldColors = null) {
    const colors = worldColors || CONFIG.COLORS;
    
    // Draw sparks
    this.sparks.forEach(s => s.draw(ctx));
    
    // Draw high streak pulse effect (subtle edge glow)
    if (this.highStreakPulse > 0) {
      ctx.save();
      ctx.globalAlpha = this.highStreakPulse * 0.3;
      const gradient = ctx.createRadialGradient(
        canvasWidth / 2, canvasHeight / 2, 0,
        canvasWidth / 2, canvasHeight / 2, Math.max(canvasWidth, canvasHeight)
      );
      gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0)');
      gradient.addColorStop(1, colors.PLAYER_GLOW || 'rgba(0, 229, 255, 0.3)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.restore();
    }
  }

  getShakeOffset() {
    return { x: this.shakeOffsetX, y: this.shakeOffsetY };
  }

  getTimeScale() {
    return this.timeScale;
  }

  isDeathSequenceComplete() {
    return this.isDying && !this.slowMotionActive;
  }

  isSlowMotionActive() {
    return this.slowMotionActive;
  }
}

export default EffectsManager;
